import React, { useState, useEffect } from 'react';
import { useGetMeetingsListQuery, useGetMeetingByIdQuery, type MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import CreatedIdsChip from '@/components/common/CreatedIdsChip';
import { Pagination } from '@/components/UI/Pagination';
import WorkspaceMeetingCard from './WorkspaceMeetingCard';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setMeetingFilter } from '@/redux/slices/uiContextSlice';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import { useWorkspaceFilterReport, type OnWorkspaceFilterChange } from '@/hooks/useWorkspaceFilterReport';

// ─── Filter chip ────────────────────────────────────────────────────────────────

const FilterChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap ${
      active
        ? 'bg-accent/10 border-accent/30 text-accent-text'
        : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-accent/25 hover:text-ui-text'
    }`}
  >
    {label}
  </button>
);

const STATUS_PRESETS = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'rescheduled', label: 'Rescheduled' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'no_show', label: 'No Show' },
];

// ─── Component ─────────────────────────────────────────────────────────────────

export interface MeetingAdvancedFilters {
  exclude_status?: string;
  exclude_meeting_type?: string;
  title_starts_with?: string;
  participant_user_id?: string;
  participant?: string;
  participant_contact_id?: string;
  min_participants?: number;
  upcoming?: boolean;
  past?: boolean;
  /** "next 3 meetings" — page size for this view (capped at the list's normal page size). */
  limit?: number;
}

interface WorkspaceMeetingListProps {
  initialStatus?: string;
  initialMeetingType?: string;
  initialSearch?: string;
  initialOrganizerUserId?: string;
  initialUpdatedByUserId?: string;
  /** Start-time window published by SAM ("tomorrow morning") — the list must match SAM's answer. */
  initialStartFrom?: string;
  initialStartTo?: string;
  /** Linked-record filter published by SAM ("meetings for this job") — e.g. engagement + id. */
  initialContextType?: string;
  initialContextEntityId?: string;
  /** Comma-separated meeting ids — show ONLY these (SAM multi-create rule FILTER_CREATED). Clearable. */
  initialIds?: string;
  initialExpandedId?: string;
  /** Filters SAM's search_meetings can publish that have no chip of their own — they still
   *  have to reach the list query, or the panel contradicts the answer SAM just gave. */
  initialAdvancedFilters?: MeetingAdvancedFilters;
  initialSortBy?: string;
  initialSortDir?: number;
  onSelectMeeting?: (meetingId: string) => void;
  onStartNoteTaker?: (contextType: string, entityId: string) => void;
  /** When set (e.g. opened directly from a notification link), show ONLY this meeting — no
   *  filters, chips, or pagination — instead of the full list. */
  singleMeetingId?: string;
  /** Called when the user leaves single-meeting mode to go back to browsing the full list. */
  onExitSingleMeeting?: () => void;
  /** Workspace mode: report a MANUALLY changed filter so it joins the "⋯" recent-views menu
   *  alongside the agent-driven views. See `useWorkspaceFilterReport`. */
  onFiltersChange?: OnWorkspaceFilterChange;
}

const WorkspaceMeetingList: React.FC<WorkspaceMeetingListProps> = ({
  initialStatus, initialMeetingType, initialSearch, initialOrganizerUserId, initialUpdatedByUserId, initialStartFrom, initialStartTo, initialContextType, initialContextEntityId, initialIds = '', initialExpandedId, initialAdvancedFilters, initialSortBy, initialSortDir, onSelectMeeting, onStartNoteTaker,
  singleMeetingId, onExitSingleMeeting: _onExitSingleMeeting, onFiltersChange,
}) => {
  const singleMeetingResult = useGetMeetingByIdQuery(singleMeetingId as string, { ...WORKSPACE_QUERY_OPTIONS, skip: !singleMeetingId });
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId ?? null);

  useEffect(() => {
    if (initialExpandedId !== undefined) setExpandedId(initialExpandedId ?? null);
  }, [initialExpandedId]);

  const dispatch = useAppDispatch();
  // Seed from Redux shared filter (set by SAM), fall back to props from UXView
  const sharedFilter = useAppSelector((s) => s.uiContext.meetingFilter);
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';

  const [status, setStatus] = useState(initialStatus ?? sharedFilter.status ?? '');
  const [meetingType, setMeetingType] = useState(initialMeetingType ?? sharedFilter.meeting_type ?? '');
  const [search] = useState(initialSearch ?? sharedFilter.search ?? '');
  const [organizerUserId, setOrganizerUserId] = useState(initialOrganizerUserId ?? sharedFilter.organizer_user_id ?? '');
  const [updatedByUserId, setUpdatedByUserId] = useState(initialUpdatedByUserId ?? sharedFilter.updated_by_user_id ?? '');
  const [startFrom, setStartFrom] = useState(initialStartFrom ?? '');
  const [startTo, setStartTo] = useState(initialStartTo ?? '');
  const [advanced, setAdvanced] = useState<MeetingAdvancedFilters>(initialAdvancedFilters ?? {});
  const [sortBy] = useState(initialSortBy ?? '');
  const [sortDir] = useState(initialSortDir ?? -1);
  const [page, setPage] = useState(0);
  const [ids, setIds] = useState(initialIds);
  // Re-sync when SAM points the list at a new set of ids; a user "clear" stays cleared until then.
  useEffect(() => {
    setIds(initialIds); setPage(0);
    // One just-created record → show it open, like the detail view a single create used to land on.
    const only = initialIds ? initialIds.split(',').filter(Boolean) : [];
    if (only.length === 1) setExpandedId(only[0]);
  }, [initialIds]);
  const idCount = ids ? ids.split(',').filter(Boolean).length : 0;
  const limit = Math.min(advanced.limit || 20, 20);

  // Keep local state in sync when SAM applies a new filter from outside
  useEffect(() => {
    if (initialStatus !== undefined) return; // controlled by prop — don't override
    setStatus(sharedFilter.status ?? '');
    setMeetingType(sharedFilter.meeting_type ?? '');
    setOrganizerUserId(sharedFilter.organizer_user_id ?? '');
    setUpdatedByUserId(sharedFilter.updated_by_user_id ?? '');
    setPage(0);
  }, [sharedFilter, initialStatus]);

  // Shared by the query and the recent-views report, so a "⋯" entry replays exactly what's shown.
  const queryArgs = { status: status || undefined, meeting_type: meetingType || undefined, search: search || undefined, organizer_user_id: organizerUserId || undefined, updated_by_user_id: updatedByUserId || undefined, start_from: startFrom || undefined, start_to: startTo || undefined, context_type: initialContextType || undefined, context_entity_id: initialContextEntityId || undefined, ids: ids || undefined, exclude_status: advanced.exclude_status || undefined, exclude_meeting_type: advanced.exclude_meeting_type || undefined, title_starts_with: advanced.title_starts_with || undefined, participant_user_id: advanced.participant_user_id || undefined, upcoming: advanced.upcoming || undefined, past: advanced.past || undefined, participant: advanced.participant || undefined, participant_contact_id: advanced.participant_contact_id || undefined, min_participants: advanced.min_participants || undefined, sort_by: sortBy || undefined, sort_dir: sortBy ? sortDir : undefined, skip: page * limit, limit };

  const { data, isLoading, isError, refetch } = useGetMeetingsListQuery(queryArgs, WORKSPACE_QUERY_OPTIONS);

  // Report user-driven filter changes (status/type chips, organizer) to the "⋯" recent-views menu.
  useWorkspaceFilterReport(queryArgs, onFiltersChange);

  // When SAM applies a filter, force a fresh fetch even if the key/args haven't changed
  // (the component may be reused from a prior render with the same filter params).
  useEffect(() => {
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFilter]);

  const meetings: MeetingFull[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleStatusChip = (id: string) => {
    const next = status === id ? '' : id;
    setStatus(next);
    setOrganizerUserId('');
    setUpdatedByUserId('');
    setPage(0);
    dispatch(setMeetingFilter({ status: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const handleCreatedByMeChip = () => {
    const next = organizerUserId === currentUserId ? '' : currentUserId;
    setOrganizerUserId(next);
    setUpdatedByUserId('');
    setStatus('');
    setPage(0);
    dispatch(setMeetingFilter({ organizer_user_id: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const handleUpdatedByMeChip = () => {
    const next = updatedByUserId === currentUserId ? '' : currentUserId;
    setUpdatedByUserId(next);
    setOrganizerUserId('');
    setStatus('');
    setPage(0);
    dispatch(setMeetingFilter({ updated_by_user_id: next || undefined, meeting_type: meetingType || undefined, search: search || undefined }));
  };

  const clearAll = () => {
    setStatus('');
    setMeetingType('');
    setOrganizerUserId('');
    setUpdatedByUserId('');
    setStartFrom('');
    setStartTo('');
    setAdvanced({});
    setIds('');
    setPage(0);
    dispatch(setMeetingFilter({}));
  };

  const activeCount = [status, meetingType, organizerUserId, updatedByUserId, startFrom || startTo, idCount ? 'x' : '', ...Object.entries(advanced).filter(([k]) => k !== 'limit').map(([, v]) => v)].filter(Boolean).length;

  if (singleMeetingId) {
    return (
      <div className="flex flex-col min-h-full space-y-2">
        {singleMeetingResult.isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
        {singleMeetingResult.isError && (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <p className="text-sm font-medium text-ui-text2">Nothing to show</p>
          </div>
        )}
        {singleMeetingResult.data && (
          <div className="grid grid-cols-1 gap-3 pb-2">
            <WorkspaceMeetingCard
              meeting={singleMeetingResult.data}
              onSelect={onSelectMeeting}
              expandedId={onSelectMeeting ? undefined : singleMeetingId}
              onExpand={onSelectMeeting ? undefined : () => {}}
              onStartNoteTaker={onStartNoteTaker}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full space-y-2">
      {/* ── Chip row ── */}
      <div className="overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
        <div className="flex gap-1.5 min-w-max">
          {/* First, so the row's sideways scroll never hides it */}
          <CreatedIdsChip count={idCount} titleKey="workspaceCreated.showAllMeetings" onClear={() => { setIds(''); setPage(0); }} />
          {STATUS_PRESETS.map((preset) => (
            <FilterChip
              key={preset.id}
              label={preset.label}
              active={status === preset.id}
              onClick={() => handleStatusChip(preset.id)}
            />
          ))}
          {!!currentUserId && (
            <FilterChip
              label="Created by Me"
              active={organizerUserId === currentUserId}
              onClick={handleCreatedByMeChip}
            />
          )}
          {!!currentUserId && (
            <FilterChip
              label="Updated by Me"
              active={updatedByUserId === currentUserId}
              onClick={handleUpdatedByMeChip}
            />
          )}
          {activeCount > 0 && (
            <button type="button" onClick={clearAll} className="flex-shrink-0 px-2.5 py-1 text-xs text-ui-text3 hover:text-accent-text transition-colors whitespace-nowrap">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Meeting list ── */}
      {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
      {isError && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-sm font-medium text-ui-text2">Nothing to show</p>
        </div>
      )}
      {!isLoading && !isError && meetings.length === 0 && (
        <p className="text-center text-ui-text3 py-8">No meetings found.</p>
      )}

      {!isLoading && (
        <div className="grid grid-cols-1 gap-3 pb-2">
          {meetings.map((m) => (
            <WorkspaceMeetingCard
              key={m.meeting_id}
              meeting={m}
              onSelect={onSelectMeeting}
              expandedId={onSelectMeeting ? undefined : expandedId}
              onExpand={onSelectMeeting ? undefined : setExpandedId}
              onStartNoteTaker={onStartNoteTaker}
            />
          ))}
        </div>
      )}

      {!singleMeetingId && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
};

export default WorkspaceMeetingList;
